// src/components/instructor/OrgFinanceView.js
//
// Organization Finance view — season overview, timesheets, Xero, earnings calculator.
// All data derived from existing bookings + organizationMembers.
// Five sub-tabs:
//   1. Season overview (monthly summary + all-instructor table)
//   2. Collective timesheets (per-instructor daily bookings)
//   3. School timesheets (per-instructor hourly sessions)
//   4. Xero reconciliation (TODO: scaffold — no Xero integration yet)
//   5. Earnings calculator (collective commission calc + school rate reference)

import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import { db, functions } from '../../firebase';
import { doc, collection, onSnapshot, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useSelectedOrganizationContext } from '../../utils/organizationSelection';
import { getOrganizationBookings } from '../../utils/booking';
import { loadOrganizationMembers } from '../../utils/organizationData';
import { buildFinanceData, calcCollectiveEarnings, SCHOOL_DAY_RATES, fmt, MONTHS } from '../../utils/financeData';

// ── Design tokens (same as OrgBookingsCRM) ─────────────────────────────────

const V = {
  surface: '#FFFFFF', s2: '#F2F1EE', s3: '#ECEAE6',
  border: '#E2E0DB', borderStrong: '#C8C5BE',
  t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95',
  green: '#1D9E75', greenBg: '#E1F5EE', greenTxt: '#085041',
  amber: '#C97D10', amberBg: '#FEF3C7', amberTxt: '#92400E',
  blue: '#2563EB', blueBg: '#EFF4FE', blueTxt: '#1A3FA0',
  red: '#DC2626', redBg: '#FEF2F2', redTxt: '#991B1B',
  purple: '#7C3AED', purpleBg: '#F5F3FF', purpleSoft: '#EDE9FE', purpleTxt: '#4C1D95',
  grayBg: '#F4F3F0', grayTxt: '#57534E',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
};
const F = "'DM Sans',sans-serif";
const FM = "'DM Mono',monospace";

// ── Main component ─────────────────────────────────────────────────────────

function OrgFinanceView({ bookings: bookingsProp, members: membersProp }) {
  const { userData } = useAuth();
  const { selectedOrganization } = useSelectedOrganizationContext(userData);
  const orgId = selectedOrganization?.organizationId || userData?.orgId || '';

  // Use shared data from shell if provided, otherwise fetch own
  const [localBookings, setLocalBookings] = useState([]);
  const [localMembers, setLocalMembers] = useState([]);
  const [localLoading, setLocalLoading] = useState(!bookingsProp);
  const [subTab, setSubTab] = useState('overview');

  const bookings = bookingsProp || localBookings;
  const members = membersProp || localMembers;
  const loading = bookingsProp === null || (bookingsProp === undefined && localLoading);

  useEffect(() => {
    if (bookingsProp !== undefined || !orgId) return; // skip if props provided
    setLocalLoading(true);
    Promise.all([
      getOrganizationBookings(db, orgId).catch(() => []),
      loadOrganizationMembers(db, orgId).catch(() => []),
    ]).then(([bks, mems]) => {
      setLocalBookings(bks || []);
      setLocalMembers(mems || []);
    }).finally(() => setLocalLoading(false));
  }, [orgId, bookingsProp]);

  const finData = useMemo(() => buildFinanceData(bookings, members), [bookings, members]);

  if (loading) return <div style={{ padding: '60px 0', textAlign: 'center', color: V.t3, fontSize: 13, fontFamily: F }}>Loading finance data...</div>;

  const { seasonStats, monthlyCol, monthlySch, colInstructors, schInstructors, instructors } = finData;

  return (
    <div style={{ fontFamily: F }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: V.t1, letterSpacing: '-0.03em' }}>Finances</div>
          <div style={{ fontSize: 13, color: V.t2, marginTop: 3 }}>Season overview · booking projections · Xero reconciliation</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn label="Open Xero →" onClick={() => window.open('https://go.xero.com', '_blank')} />
        </div>
      </div>

      {/* Pass 83C — this hidden Finances surface derives figures from BOOKINGS + member rates. It is an
          ESTIMATE / BOOKING PROJECTION, NOT authoritative timekeeping or payroll. The canonical worked-time
          record is the staff Timesheet (Operations → Team → Timesheets). */}
      <div data-testid="finance-projection-warning" style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: '#92400E' }}>
        <strong>Estimate / booking projection — NOT authoritative timekeeping or payroll.</strong> These figures are
        derived from bookings + reference rates. Authoritative worked time lives in the staff <em>Timesheet</em> board
        (Team → Timesheets). No payroll or Xero submission is wired here.
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 8, marginBottom: 20 }}>
        <StatCard label="Total revenue (season)" value={fmt(seasonStats.totalRevenue)} color={V.green} sub="gross lesson income" />
        <StatCard label="SAS commission" value={fmt(seasonStats.estimatedCommission)} sub="~22% estimate" />
        <StatCard label="Collective payouts" value={fmt(seasonStats.colSeasonEarn)} color={V.purple} sub={`${colInstructors.length} instructors`} />
        <StatCard label="School payroll" value={fmt(seasonStats.schSeasonEarn)} sub={`${schInstructors.length} instructors`} />
        <StatCard label="Invoices unpaid" value={seasonStats.unpaidCount} color={V.amber} sub="needs follow-up" />
        <StatCard label="WPI deductions" value={fmt(seasonStats.estimatedWPI)} sub="5% across collective" />
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${V.border}`, marginBottom: 16 }}>
        {[
          { key: 'overview', label: 'Season overview' },
          { key: 'instructors', label: 'Instructors' },
          { key: 'collective', label: 'Collective booking projection' },
          { key: 'school', label: 'School booking projection' },
          { key: 'xero', label: 'Xero reconciliation' },
          { key: 'calc', label: 'Earnings calculator' },
        ].map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)} style={{
            fontSize: 12, padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: F,
            color: subTab === t.key ? V.t1 : V.t3,
            borderBottom: subTab === t.key ? `2px solid ${V.t1}` : '2px solid transparent',
            fontWeight: subTab === t.key ? 500 : 400, marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      {subTab === 'overview' && <OverviewTab monthlyCol={monthlyCol} monthlySch={monthlySch} instructors={instructors} />}
      {subTab === 'instructors' && <InstructorsTab instructors={instructors} bookings={bookings} onViewTimesheet={(inst) => { setSubTab(inst.isCollective ? 'collective' : 'school'); }} />}
      {subTab === 'collective' && <TimesheetTab instructors={colInstructors} isCollective />}
      {subTab === 'school' && <TimesheetTab instructors={schInstructors} />}
      {subTab === 'xero' && <XeroTab orgId={orgId} colInstructors={colInstructors} bookings={bookings} />}
      {subTab === 'calc' && <CalcTab />}
    </div>
  );
}

// ── Season Overview Tab ────────────────────────────────────────────────────

function OverviewTab({ monthlyCol, monthlySch, instructors }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? instructors : instructors.filter(i => filter === 'collective' ? i.isCollective : !i.isCollective);

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Collective monthly */}
        <Card title={<>Collective — monthly earnings <Badge label={`${instructors.filter(i => i.isCollective).length} instructors`} color="purple" /></>}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Month', 'Gross earnings', 'FD / HD', 'Net payout'].map(h => <Th key={h} right={h !== 'Month'}>{h}</Th>)}</tr></thead>
            <tbody>
              {MONTHS.map(m => {
                const d = monthlyCol[m];
                return <tr key={m}><Td>{m}</Td><Td right mono>{fmt(d.earn)}</Td><Td right mono>{d.fd} / {d.hd}</Td><Td right mono bold>{fmt(d.earn)}</Td></tr>;
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <Td>Season</Td>
                <Td right mono style={{ color: V.green }}>{fmt(MONTHS.reduce((s, m) => s + monthlyCol[m].earn, 0))}</Td>
                <Td right mono>{MONTHS.reduce((s, m) => s + monthlyCol[m].fd, 0)} FD</Td>
                <Td right mono>{fmt(MONTHS.reduce((s, m) => s + monthlyCol[m].earn, 0))}</Td>
              </tr>
            </tfoot>
          </table>
        </Card>

        {/* School monthly */}
        <Card title={<>School — monthly payroll <Badge label={`${instructors.filter(i => !i.isCollective).length} instructors`} color="blue" /></>}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>{['Month', 'Hours', 'Sessions', 'Gross pay'].map(h => <Th key={h} right={h !== 'Month'}>{h}</Th>)}</tr></thead>
            <tbody>
              {MONTHS.map(m => {
                const d = monthlySch[m];
                return <tr key={m}><Td>{m}</Td><Td right mono>{d.hours > 0 ? `${Math.round(d.hours)}h` : '—'}</Td><Td right mono>{d.lessons || '—'}</Td><Td right mono>{fmt(d.gross)}</Td></tr>;
              })}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <Td>Season</Td>
                <Td right mono>{Math.round(MONTHS.reduce((s, m) => s + monthlySch[m].hours, 0))}h</Td>
                <Td right mono>{MONTHS.reduce((s, m) => s + monthlySch[m].lessons, 0)}</Td>
                <Td right mono style={{ color: V.green }}>{fmt(MONTHS.reduce((s, m) => s + monthlySch[m].gross, 0))}</Td>
              </tr>
            </tfoot>
          </table>
        </Card>
      </div>

      {/* All instructors table */}
      <Card title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span>All instructors — season at a glance</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', 'collective', 'school'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 4, border: `1px solid ${filter === f ? V.t1 : 'transparent'}`, background: filter === f ? V.s3 : 'none', color: V.t2, cursor: 'pointer', fontFamily: F, textTransform: 'capitalize' }}>{f}</button>
          ))}
        </div>
      </div>}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr>
            <Th>Instructor</Th><Th>Type</Th>
            {MONTHS.map(m => <Th key={m} right>{m.slice(0, 3)}</Th>)}
            <Th right>Season total</Th><Th>Payment</Th>
          </tr></thead>
          <tbody>
            {filtered.map(inst => (
              <tr key={inst.userId} onMouseEnter={e => e.currentTarget.style.background = V.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <Td bold style={{ color: inst.isCollective ? V.purple : V.t1 }}>{inst.name}</Td>
                <Td><Badge label={inst.isCollective ? 'Collective' : 'School'} color={inst.isCollective ? 'purple' : 'blue'} /></Td>
                {MONTHS.map(m => <Td key={m} right mono style={{ fontSize: 11 }}>{inst.months[m].grossEarn > 0 ? fmt(inst.months[m].grossEarn) : '—'}</Td>)}
                <Td right mono bold style={{ color: V.green }}>{inst.seasonEarn > 0 ? fmt(inst.seasonEarn) : '—'}</Td>
                <Td><Badge label={inst.seasonEarn > 0 ? 'Active' : '—'} color={inst.seasonEarn > 0 ? 'green' : 'gray'} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

// ── Timesheet Tab (shared by Collective + School) ──────────────────────────

// ── Instructors Tab ────────────────────────────────────────────────────────

function InstructorsTab({ instructors, bookings, onViewTimesheet }) {
  const [pool, setPool] = useState('internal');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const filtered = instructors.filter(i => {
    if (pool === 'internal' && i.isCollective) return false;
    if (pool === 'collective' && !i.isCollective) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!i.name.toLowerCase().includes(q) && !i.discipline.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Build weekly availability from bookings (current week)
  const weekAvail = useMemo(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);

    const map = {};
    for (const inst of instructors) map[inst.userId] = [false, false, false, false, false, false, false];

    for (const b of bookings) {
      if (b.status === 'canceled' || !b.instructorId) continue;
      const s = b.startAt?.toDate ? b.startAt.toDate() : b.startAt?.seconds ? new Date(b.startAt.seconds * 1000) : new Date(b.startAt);
      if (!s) continue;
      const diff = Math.floor((s - monday) / 86400000);
      if (diff >= 0 && diff < 7 && map[b.instructorId]) {
        map[b.instructorId][diff] = true;
      }
    }
    return map;
  }, [instructors, bookings]);

  const isCollective = pool === 'collective';

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        {/* Pool switcher */}
        <div style={{ display: 'flex', gap: 2, background: V.surface, border: `1px solid ${V.border}`, borderRadius: 8, padding: 2 }}>
          {[{ key: 'internal', label: 'SAS school' }, { key: 'collective', label: 'Collective' }].map(p => (
            <button key={p.key} onClick={() => { setPool(p.key); setExpandedId(null); }} style={{
              padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: pool === p.key ? 600 : 400,
              color: pool === p.key ? '#fff' : V.t3, border: 'none',
              background: pool === p.key ? (p.key === 'collective' ? V.purple : V.t1) : 'none',
              fontFamily: F, cursor: 'pointer',
            }}>{p.label}</button>
          ))}
        </div>
        {/* Search */}
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or discipline…"
          style={{ fontFamily: F, fontSize: 12, padding: '6px 12px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none', width: 220, color: V.t1 }} />
      </div>

      {/* Info bar for collective */}
      {isCollective && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: V.surface, border: `1px solid ${V.purpleBorder}`, borderRadius: 12, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: V.purpleTxt, marginBottom: 6 }}>Commission to SAS</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ rate: '25%', label: 'Stage 1', bg: V.amberBg, c: V.amberTxt }, { rate: '20%', label: 'Stage 2', bg: V.blueBg, c: V.blueTxt }, { rate: '18%', label: 'Stage 3', bg: V.greenBg, c: V.greenTxt }].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '8px 14px', background: s.bg, borderRadius: 8 }}>
                  <div style={{ fontSize: 18, fontWeight: 600, fontFamily: FM, color: s.c }}>{s.rate}</div>
                  <div style={{ fontSize: 10, color: s.c, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 11, color: V.t2, maxWidth: 340, lineHeight: 1.6 }}>
            Commission applied after consumption tax (÷11) and card processing (3.6%) are deducted from lesson price. Passed work earns originating instructor 10%.
          </div>
        </div>
      )}

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 10 }}>
        {filtered.map(inst => (
          <InstructorCard key={inst.userId} inst={inst} isCollective={isCollective} weekAvail={weekAvail[inst.userId]}
            expanded={expandedId === inst.userId} onToggle={() => setExpandedId(expandedId === inst.userId ? null : inst.userId)}
            onViewTimesheet={() => onViewTimesheet(inst)} />
        ))}
        {filtered.length === 0 && <div style={{ gridColumn: '1/-1', padding: 24, textAlign: 'center', color: V.t3, fontSize: 12 }}>No instructors found</div>}
      </div>
    </div>
  );
}

function InstructorCard({ inst, isCollective, weekAvail, expanded, onToggle, onViewTimesheet }) {
  const initials = inst.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const avatarBg = isCollective ? `linear-gradient(135deg,${V.purple},#8B5CF6)` : `linear-gradient(135deg,${V.blue},#6366F1)`;

  // Parse discipline for tags
  const discTags = [];
  const d = (inst.discipline || '').toLowerCase();
  if (d.includes('ski')) discTags.push('Ski');
  if (d.includes('sb') || d.includes('snowboard')) discTags.push('Snowboard');
  if (discTags.length === 0 && d) discTags.push(inst.discipline);

  const rateDisplay = isCollective
    ? (inst.rate ? `¥${(inst.rate / 1000).toFixed(0)}k/day` : '')
    : (inst.rate ? `¥${inst.rate.toLocaleString()}/hr` : '');

  return (
    <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, boxShadow: V.shadow, overflow: 'hidden', borderLeft: `3px solid ${isCollective ? V.purple : V.blue}` }}>
      {/* Card content */}
      <div onClick={onToggle} style={{ padding: 14, cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background = V.s2}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0 }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: V.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inst.name}</div>
            <div style={{ fontSize: 11, color: V.t3, fontFamily: FM, marginTop: 1 }}>{inst.discipline}</div>
          </div>
        </div>
        {/* Tags + rate */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
          {discTags.map(t => <Badge key={t} label={t} color={t === 'Ski' ? 'blue' : t === 'Snowboard' ? 'purple' : 'gray'} />)}
          {rateDisplay && <span style={{ fontSize: 11, color: V.t3, fontFamily: FM }}>{rateDisplay}</span>}
        </div>
        {/* Season earnings + week bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600, fontFamily: FM, color: inst.seasonEarn > 0 ? V.green : V.t3 }}>{inst.seasonEarn > 0 ? fmt(inst.seasonEarn) : '—'}</div>
          {/* Week availability bar */}
          {weekAvail && (
            <div style={{ display: 'flex', gap: 2 }} title="This week: Mon–Sun">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, i) => (
                <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: weekAvail[i] ? V.green : V.s3, border: `1px solid ${weekAvail[i] ? V.green : V.border}` }} title={`${label}: ${weekAvail[i] ? 'Booked' : 'Free'}`} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${V.border}`, padding: 14, background: V.s2 }}>
          {/* Monthly earnings */}
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.t3, marginBottom: 6 }}>Season earnings by month</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6, marginBottom: 12 }}>
            {MONTHS.map(m => (
              <div key={m} style={{ textAlign: 'center', padding: '6px 4px', background: V.surface, borderRadius: 6, border: `1px solid ${V.border}` }}>
                <div style={{ fontSize: 9, color: V.t3, marginBottom: 2 }}>{m.slice(0, 3)}</div>
                <div style={{ fontSize: 12, fontWeight: 600, fontFamily: FM, color: inst.months[m].grossEarn > 0 ? V.t1 : V.t3 }}>{inst.months[m].grossEarn > 0 ? fmt(inst.months[m].grossEarn) : '—'}</div>
              </div>
            ))}
          </div>
          {/* Summary row */}
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: V.t2, marginBottom: 12, flexWrap: 'wrap' }}>
            <span>{isCollective ? `${inst.seasonFD} FD · ${inst.seasonHD} HD` : `${Math.round(inst.seasonHours)}h · ${inst.seasonLessons} sessions`}</span>
            <span>·</span>
            <span style={{ color: V.green, fontWeight: 500 }}>Season: {fmt(inst.seasonEarn)}</span>
          </div>
          {/* Action */}
          <button onClick={onViewTimesheet} style={{ width: '100%', padding: '8px', borderRadius: 8, border: `1px solid ${V.borderStrong}`, background: V.surface, color: V.t1, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: F, textAlign: 'center' }}>
            View timesheet →
          </button>
        </div>
      )}
    </div>
  );
}

function TimesheetTab({ instructors, isCollective }) {
  const [selectedId, setSelectedId] = useState(instructors[0]?.userId || '');
  const [month, setMonth] = useState('December');
  const inst = instructors.find(i => i.userId === selectedId) || instructors[0];
  const mo = inst?.months[month];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '230px 1fr', gap: 16, alignItems: 'start' }}>
      {/* Sidebar */}
      <Card title={isCollective ? 'Collective instructors' : 'School instructors'}>
        <div style={{ padding: 6, maxHeight: 500, overflowY: 'auto' }}>
          {instructors.map(i => (
            <div key={i.userId} onClick={() => setSelectedId(i.userId)} style={{
              padding: '7px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 2,
              background: i.userId === selectedId ? (isCollective ? V.purpleSoft : V.blueBg) : 'transparent',
            }}
              onMouseEnter={e => { if (i.userId !== selectedId) e.currentTarget.style.background = V.s2; }}
              onMouseLeave={e => { if (i.userId !== selectedId) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: i.userId === selectedId ? (isCollective ? V.purple : V.blueTxt) : V.t1 }}>{i.name}</div>
              <div style={{ fontSize: 10, color: V.t3, fontFamily: FM, marginTop: 1 }}>
                {isCollective ? `Stage ${i.stage}` : ''} · {i.seasonEarn > 0 ? fmt(i.seasonEarn) : 'No data'}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Detail */}
      <div>
        <Card title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600 }}>{inst?.name || '—'}</span>
            {isCollective && <Badge label={`Stage ${inst?.stage}`} color={inst?.stage === 3 ? 'green' : inst?.stage === 2 ? 'blue' : 'amber'} />}
          </div>
          <select value={month} onChange={e => setMonth(e.target.value)} style={{ fontFamily: F, fontSize: 12, padding: '5px 10px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none' }}>
            {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>}>
          {/* Summary strip */}
          <div style={{ display: 'grid', gridTemplateColumns: isCollective ? 'repeat(5,1fr)' : 'repeat(5,1fr)', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${V.border}` }}>
            {isCollective ? (
              <>
                <MiniStat label="Full days" value={mo?.fd || 0} />
                <MiniStat label="Half days" value={mo?.hd || 0} />
                <MiniStat label="Gross earnings" value={fmt(mo?.grossEarn)} color={V.green} />
                <MiniStat label="WPI (5%)" value={mo?.grossEarn > 0 ? fmt(Math.round(mo.grossEarn * 0.05)) : '—'} color={V.amber} />
                <MiniStat label="Net payout" value={mo?.grossEarn > 0 ? fmt(Math.round(mo.grossEarn * 0.95)) : '—'} />
              </>
            ) : (
              <>
                <MiniStat label="Hours" value={mo?.hours > 0 ? `${Math.round(mo.hours)}` : '—'} />
                <MiniStat label="Sessions" value={mo?.lessons || '—'} />
                <MiniStat label="Gross pay" value={fmt(mo?.grossEarn)} color={V.green} />
                <MiniStat label="Rate/hr" value={inst?.rate ? `¥${inst.rate.toLocaleString()}` : '—'} />
                <MiniStat label="Net payout" value={fmt(mo?.grossEarn)} />
              </>
            )}
          </div>

          {/* Booking rows */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              <Th>Date</Th><Th>Client</Th>
              {isCollective ? <><Th center>FD</Th><Th center>HD</Th></> : <><Th>Start</Th><Th right>Hrs</Th></>}
              <Th right>Earnings</Th><Th>Status</Th>
            </tr></thead>
            <tbody>
              {mo?.bookings?.length > 0 ? mo.bookings.map((b, i) => (
                <tr key={i} onMouseEnter={e => e.currentTarget.style.background = V.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Td mono style={{ fontSize: 11 }}>{b.dateStr}</Td>
                  <Td>{b.client}</Td>
                  {isCollective ? (
                    <><Td center>{b.fd ? <Badge label="FD" color="green" /> : ''}</Td><Td center>{b.hd ? <Badge label="HD" color="blue" /> : ''}</Td></>
                  ) : (
                    <><Td mono style={{ fontSize: 11 }}>{b.startTime}</Td><Td right mono>{b.hours}</Td></>
                  )}
                  <Td right mono bold>{fmt(b.earn)}</Td>
                  <Td><Badge label={b.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'} color={b.paymentStatus === 'paid' ? 'green' : 'amber'} /></Td>
                </tr>
              )) : (
                <tr><td colSpan={isCollective ? 6 : 6} style={{ textAlign: 'center', color: V.t3, fontStyle: 'italic', padding: 20 }}>No entries for {month}</td></tr>
              )}
            </tbody>
          </table>

          {/* TODO: Deductions section — no deduction records exist yet */}
          {isCollective && mo?.grossEarn > 0 && (
            <div style={{ padding: '12px 16px', background: V.s2, borderTop: `1px solid ${V.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: V.t3, marginBottom: 8 }}>Deductions this month</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <DeductionChip label="WPI (5%)" value={fmt(Math.round(mo.grossEarn * 0.05))} />
                {/* TODO: These deductions are not yet tracked in Firestore — need a deductions collection */}
                <span style={{ fontSize: 10, color: V.t3, fontStyle: 'italic', alignSelf: 'center' }}>Other deductions (Niseko pass, rent, uniform) not yet tracked</span>
              </div>
            </div>
          )}
        </Card>

        {/* Season totals */}
        <div style={{ marginTop: 12 }}>
          <Card title="Season totals">
            <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              <div><div style={{ fontSize: 10, color: V.t3, marginBottom: 3 }}>Season gross</div><div style={{ fontSize: 18, fontWeight: 600, fontFamily: FM, color: V.green }}>{fmt(inst?.seasonEarn)}</div></div>
              <div><div style={{ fontSize: 10, color: V.t3, marginBottom: 3 }}>{isCollective ? 'Full days' : 'Total hours'}</div><div style={{ fontSize: 18, fontWeight: 600, fontFamily: FM }}>{isCollective ? inst?.seasonFD : Math.round(inst?.seasonHours || 0)}</div></div>
              <div><div style={{ fontSize: 10, color: V.t3, marginBottom: 3 }}>{isCollective ? 'Half days' : 'Sessions'}</div><div style={{ fontSize: 18, fontWeight: 600, fontFamily: FM }}>{isCollective ? inst?.seasonHD : inst?.seasonLessons}</div></div>
              <div><div style={{ fontSize: 10, color: V.t3, marginBottom: 3 }}>Est. deductions</div><div style={{ fontSize: 18, fontWeight: 600, fontFamily: FM, color: V.amber }}>{isCollective ? fmt(Math.round((inst?.seasonEarn || 0) * 0.05)) : '—'}</div></div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ── Xero Reconciliation Tab ────────────────────────────────────────────────

function XeroTab({ orgId, colInstructors, bookings }) {
  const [xeroDoc, setXeroDoc] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('December');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [banner, setBanner] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [matchingId, setMatchingId] = useState(null);

  // ── Subscribe to Xero integration doc ──────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      doc(db, 'organizations', orgId, 'integrations', 'xero'),
      (snap) => {
        setXeroDoc(snap.exists() ? snap.data() : null);
        setXeroLoading(false);
      },
      () => setXeroLoading(false)
    );
    return unsub;
  }, [orgId]);

  // ── Subscribe to xeroInvoices subcollection ────────────────────────
  useEffect(() => {
    if (!orgId) return;
    const unsub = onSnapshot(
      collection(db, 'organizations', orgId, 'xeroInvoices'),
      (snap) => {
        const docs = [];
        snap.forEach((d) => docs.push({ id: d.id, ...d.data() }));
        setInvoices(docs);
      }
    );
    return unsub;
  }, [orgId]);

  // ── Check for xeroConnected=true on mount ──────────────────────────
  useEffect(() => {
    const full = window.location.search + window.location.hash;
    if (full.includes('xeroConnected=true')) {
      setBanner({ type: 'success', msg: 'Xero connected successfully' });
      const t = setTimeout(() => {
        setBanner(null);
        const clean = window.location.href.replace(/[?&]xeroConnected=true/, '').replace(/xeroConnected=true&?/, '');
        window.history.replaceState(null, '', clean);
      }, 4000);
      return () => clearTimeout(t);
    }
  }, []);

  // ── Filter invoices by selectedMonth (using syncedAt) ──────────────
  const monthInvoices = useMemo(() => {
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const targetIdx = monthNames.indexOf(selectedMonth);
    if (targetIdx === -1) return invoices;
    return invoices.filter((inv) => {
      const ts = inv.syncedAt?.toDate ? inv.syncedAt.toDate() : inv.syncedAt?.seconds ? new Date(inv.syncedAt.seconds * 1000) : null;
      if (!ts) return false;
      return ts.getMonth() === targetIdx;
    });
  }, [invoices, selectedMonth]);

  // ── Helpers for callable errors ────────────────────────────────────
  function handleCallableError(err) {
    const msg = err.message || '';
    if (/token|refresh/i.test(msg)) {
      setErrorBanner({ msg: 'Xero session expired — reconnect to continue.', showReconnect: true });
    } else if (/tenant/i.test(msg)) {
      setErrorBanner({ msg: 'Xero organisation not found — please disconnect and reconnect.' });
    } else {
      setErrorBanner({ msg: 'Xero error: ' + msg });
    }
  }

  // ── Sync invoices ──────────────────────────────────────────────────
  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    setErrorBanner(null);
    try {
      const fn = httpsCallable(functions, 'xeroSyncInvoices');
      const res = await fn({ orgId, month: selectedMonth });
      const r = res.data;
      setSyncResult(r);
      if (r.synced === 0) {
        setSyncResult({ ...r, type: 'empty' });
      } else if (r.matched === 0) {
        setSyncResult({ ...r, type: 'warning' });
      } else {
        setSyncResult({ ...r, type: 'success' });
      }
    } catch (err) {
      handleCallableError(err);
    } finally {
      setSyncing(false);
    }
  }

  // ── Disconnect ─────────────────────────────────────────────────────
  async function handleDisconnect() {
    if (!window.confirm('Disconnect Xero? This will stop invoice syncing.')) return;
    setErrorBanner(null);
    try {
      const fn = httpsCallable(functions, 'xeroDisconnect');
      await fn({ orgId });
    } catch (err) {
      handleCallableError(err);
    }
  }

  // ── Manual match ───────────────────────────────────────────────────
  async function handleManualMatch(invoiceId, bookingId) {
    try {
      await updateDoc(doc(db, 'organizations', orgId, 'xeroInvoices', invoiceId), {
        matchedBookingId: bookingId,
        matchConfidence: 'manual',
      });
      setMatchingId(null);
    } catch (err) {
      handleCallableError(err);
    }
  }

  // ── Connect redirect ──────────────────────────────────────────────
  // Pass 82A.2 — the connect flow is now authenticated + org-authorized. We first call xeroBeginConnect (onCall, which
  // carries the caller's auth token and requires owner/admin of this org); it mints an unforgeable one-time OAuth state
  // bound to (org, actor). We then navigate to xeroAuthRedirect with ONLY that state nonce — never a raw orgId.
  async function handleConnect() {
    try {
      const begin = httpsCallable(functions, 'xeroBeginConnect');
      const { data } = await begin({ orgId });
      const base = process.env.REACT_APP_FUNCTIONS_BASE_URL || '';
      window.location.href = base + '/xeroAuthRedirect?state=' + encodeURIComponent(data.state);
    } catch (err) {
      handleCallableError(err);
    }
  }

  // ── Recent bookings for manual matching ────────────────────────────
  const recentBookings = useMemo(() => {
    const toMs = (v) => v?.toDate ? v.toDate().getTime() : v instanceof Date ? v.getTime() : v?.seconds ? v.seconds * 1000 : 0;
    return [...(bookings || [])]
      .sort((a, b) => toMs(b.startAt) - toMs(a.startAt))
      .slice(0, 20);
  }, [bookings]);

  if (xeroLoading) {
    return <div style={{ padding: 40, textAlign: 'center', color: V.t3, fontSize: 13 }}>Loading Xero status...</div>;
  }

  const isConnected = xeroDoc && xeroDoc.status === 'connected';

  // ── Banners ────────────────────────────────────────────────────────
  const BannerRow = () => (
    <>
      {banner && (
        <div style={{ padding: '10px 16px', background: V.greenBg, color: V.greenTxt, fontSize: 12, borderRadius: 8, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: V.greenTxt, fontSize: 14, padding: '0 4px' }}>&times;</button>
        </div>
      )}
      {errorBanner && (
        <div style={{ padding: '10px 16px', background: V.redBg, color: V.redTxt, fontSize: 12, borderRadius: 8, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>{errorBanner.msg}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {errorBanner.showReconnect && <Btn label="Reconnect →" primary onClick={handleConnect} />}
            <button onClick={() => setErrorBanner(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: V.redTxt, fontSize: 14, padding: '0 4px' }}>&times;</button>
          </div>
        </div>
      )}
    </>
  );

  // ═══════════════════════════════════════════════════════════════════
  // DISCONNECTED STATE
  // ═══════════════════════════════════════════════════════════════════
  if (!isConnected) {
    return (
      <div>
        <BannerRow />
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <Card title="Connect Xero">
            <div style={{ padding: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: V.t1, marginBottom: 8 }}>Connect Xero</div>
              <div style={{ fontSize: 13, color: V.t2, marginBottom: 16, lineHeight: 1.6 }}>
                Sync invoices to bookings, update payment status automatically, and track outstanding amounts across your organisation.
              </div>
              <ol style={{ margin: '0 0 20px 0', padding: '0 0 0 18px', fontSize: 12, color: V.t2, lineHeight: 2 }}>
                <li><strong>Authorise</strong> — you'll be redirected to Xero and back automatically</li>
                <li><strong>Sync</strong> — pull invoices for any month and match to bookings</li>
                <li><strong>Reconcile</strong> — chase unpaid, raise invoices, export instructor payouts</li>
              </ol>
              <Btn label="Connect Xero →" primary onClick={handleConnect} />
              <div style={{ fontSize: 11, color: V.t3, marginTop: 10 }}>
                You'll be redirected to Xero to authorise, then returned here automatically.
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // CONNECTED STATE
  // ═══════════════════════════════════════════════════════════════════
  const connectedDate = xeroDoc.connectedAt?.toDate ? xeroDoc.connectedAt.toDate() : xeroDoc.connectedAt?.seconds ? new Date(xeroDoc.connectedAt.seconds * 1000) : null;
  const connectedLabel = connectedDate
    ? `Since ${connectedDate.getDate()} ${connectedDate.toLocaleString('en-GB', { month: 'long' })} ${connectedDate.getFullYear()}`
    : '';

  // ── Invoice totals ─────────────────────────────────────────────────
  const totalDue = monthInvoices.reduce((s, inv) => s + (inv.xeroAmountDue || 0), 0);
  const totalPaid = monthInvoices.reduce((s, inv) => s + (inv.xeroAmountPaid || 0), 0);
  const totalOutstanding = totalDue - totalPaid;
  const unpaidCount = monthInvoices.filter((inv) => inv.xeroStatus !== 'PAID').length;

  return (
    <div>
      <BannerRow />

      {/* ── Connected header bar ──────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: V.green }} />
          <span style={{ fontSize: 12, color: V.green, fontWeight: 500 }}>Connected</span>
          {connectedLabel && <span style={{ fontSize: 11, color: V.t3 }}>{connectedLabel}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} style={{ fontFamily: F, fontSize: 12, padding: '5px 10px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none' }}>
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button onClick={handleSync} disabled={syncing} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, border: `1px solid ${V.t1}`, background: V.t1, color: '#fff', cursor: syncing ? 'default' : 'pointer', fontFamily: F, fontWeight: 500, opacity: syncing ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            {syncing && <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />}
            Sync invoices
          </button>
          <Btn label="Disconnect" ghost onClick={handleDisconnect} />
        </div>
      </div>

      {/* ── Sync status banners ───────────────────────────────────── */}
      {syncing && (
        <div style={{ padding: '10px 16px', background: V.blueBg, color: V.blueTxt, fontSize: 12, borderRadius: 8, marginBottom: 12 }}>
          Syncing {selectedMonth} invoices from Xero...
        </div>
      )}
      {syncResult && !syncing && (
        <div style={{
          padding: '10px 16px', fontSize: 12, borderRadius: 8, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: syncResult.type === 'success' ? V.greenBg : syncResult.type === 'warning' ? V.amberBg : V.grayBg,
          color: syncResult.type === 'success' ? V.greenTxt : syncResult.type === 'warning' ? V.amberTxt : V.grayTxt,
        }}>
          <span>
            {syncResult.type === 'success' && `Synced ${syncResult.synced} invoices — ${syncResult.matched} matched to bookings, ${syncResult.unmatched} unmatched, ${syncResult.paid} marked paid`}
            {syncResult.type === 'warning' && `Synced ${syncResult.synced} invoices but none matched to bookings. Check booking references match Xero invoice numbers.`}
            {syncResult.type === 'empty' && `No invoices found in Xero for ${selectedMonth}.`}
          </span>
          <button onClick={() => setSyncResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '0 4px', color: 'inherit' }}>&times;</button>
        </div>
      )}

      {/* ── Invoice table ─────────────────────────────────────────── */}
      <Card title={`Xero invoices — ${selectedMonth}`}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <Th>Invoice #</Th><Th>Client</Th><Th>Booking</Th>
              <Th right>Amount</Th><Th right>Paid</Th><Th right>Outstanding</Th>
              <Th>Status</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {monthInvoices.length > 0 ? monthInvoices.map((inv) => {
              const out = (inv.xeroAmountDue || 0) - (inv.xeroAmountPaid || 0);
              const isVoided = inv.xeroStatus === 'VOIDED';
              return (
                <tr key={inv.id} style={isVoided ? { textDecoration: 'line-through' } : undefined}
                  onMouseEnter={(e) => e.currentTarget.style.background = V.s2}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <Td mono style={{ fontSize: 11 }}>{inv.xeroInvoiceNumber || '—'}</Td>
                  <Td>{inv.xeroContactName || '—'}</Td>
                  <Td>
                    {inv.matchConfidence === 'exact' || inv.matchConfidence === 'fuzzy' || inv.matchConfidence === 'manual' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ background: V.greenBg, color: V.greenTxt, borderRadius: 4, fontSize: 10, padding: '2px 7px', fontFamily: FM }}>
                          {(inv.matchedBookingId || '').slice(-6)}
                        </span>
                        {inv.matchConfidence === 'fuzzy' && <span style={{ fontSize: 11, color: V.t3, fontStyle: 'italic' }}>fuzzy</span>}
                        {inv.matchConfidence === 'manual' && <span style={{ fontSize: 11, color: V.t3, fontStyle: 'italic' }}>manual</span>}
                      </span>
                    ) : (
                      <Badge label="Unmatched" color="amber" />
                    )}
                  </Td>
                  <Td right mono>{fmt(inv.xeroAmountDue)}</Td>
                  <Td right mono>{inv.xeroAmountPaid > 0 ? fmt(inv.xeroAmountPaid) : '—'}</Td>
                  <Td right mono style={{ color: out > 0 ? V.amber : V.t3, fontWeight: out > 0 ? 500 : 400 }}>
                    {out > 0 ? fmt(out) : '—'}
                  </Td>
                  <Td>
                    {inv.xeroStatus === 'PAID' && <Badge label="PAID" color="green" />}
                    {inv.xeroStatus === 'AUTHORISED' && <Badge label="AUTHORISED" color="amber" />}
                    {inv.xeroStatus === 'VOIDED' && <Badge label="VOIDED" color="red" />}
                    {!['PAID', 'AUTHORISED', 'VOIDED'].includes(inv.xeroStatus) && <Badge label={inv.xeroStatus || '—'} color="gray" />}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {inv.xeroStatus === 'AUTHORISED' && inv.matchedBookingId && (
                        <button onClick={() => {
                          const subject = encodeURIComponent('Payment reminder — SAS Niseko ' + (inv.xeroInvoiceNumber || ''));
                          const body = encodeURIComponent(
                            'Hi,\n\nThis is a friendly reminder that payment of ¥' + Math.round(inv.xeroAmountDue || 0).toLocaleString() +
                            ' is outstanding for your SAS Niseko booking (' + (inv.xeroInvoiceNumber || '') +
                            ').\n\nPlease arrange payment at your earliest convenience.\n\nThank you,\nSAS Niseko Team'
                          );
                          window.open('mailto:' + (inv.requesterEmail || '') + '?subject=' + subject + '&body=' + body);
                        }} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', background: 'none', color: V.t1, cursor: 'pointer', fontFamily: F, fontWeight: 500 }}>
                          Chase →
                        </button>
                      )}
                      {inv.matchConfidence === 'unmatched' && (
                        matchingId === inv.id ? (
                          <select autoFocus onChange={(e) => { if (e.target.value) handleManualMatch(inv.id, e.target.value); }}
                            onBlur={() => setMatchingId(null)}
                            style={{ fontFamily: F, fontSize: 11, padding: '3px 6px', border: `1px solid ${V.borderStrong}`, borderRadius: 6, background: V.surface, outline: 'none', maxWidth: 160 }}>
                            <option value="">Select booking...</option>
                            {recentBookings.map((b) => {
                              const d = b.startAt?.toDate ? b.startAt.toDate() : b.startAt?.seconds ? new Date(b.startAt.seconds * 1000) : null;
                              const label = (b.requesterName || 'Client') + (d ? ' · ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
                              return <option key={b.id} value={b.id}>{label}</option>;
                            })}
                          </select>
                        ) : (
                          <button onClick={() => setMatchingId(inv.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', background: 'none', color: V.t1, cursor: 'pointer', fontFamily: F, fontWeight: 500 }}>
                            Match →
                          </button>
                        )
                      )}
                      <button onClick={() => window.open('https://go.xero.com/AccountsReceivable/View.aspx?invoiceID=' + inv.xeroInvoiceId, '_blank')}
                        style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', background: 'none', color: V.blue, cursor: 'pointer', fontFamily: F }}>
                        Xero ↗
                      </button>
                    </div>
                  </Td>
                </tr>
              );
            }) : (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: V.t3, fontSize: 12, fontStyle: 'italic' }}>No invoices synced for {selectedMonth}. Click "Sync invoices" to pull from Xero.</td></tr>
            )}
          </tbody>
          {monthInvoices.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 600 }}>
                <Td>Total</Td><Td /><Td />
                <Td right mono>{fmt(totalDue)}</Td>
                <Td right mono>{fmt(totalPaid)}</Td>
                <Td right mono style={{ color: totalOutstanding > 0 ? V.amber : V.t3 }}>{fmt(totalOutstanding)}</Td>
                <Td />
                <Td>{unpaidCount > 0 && <span style={{ color: V.amber }}>{unpaidCount} unpaid</span>}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>

      {/* ── Instructor payout table ───────────────────────────────── */}
      <div style={{ marginTop: 16 }}>
        <Card title={`Instructor payouts — ${selectedMonth}`}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <Th>Instructor</Th><Th>Days</Th><Th right>Gross</Th>
                <Th right>WPI (5%)</Th><Th right>Net payout</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {colInstructors.filter((inst) => inst.months[selectedMonth]?.grossEarn > 0).length > 0
                ? colInstructors.filter((inst) => inst.months[selectedMonth]?.grossEarn > 0).map((inst) => {
                    const mo = inst.months[selectedMonth];
                    const wpi = Math.round(mo.grossEarn * 0.05);
                    const net = mo.grossEarn - wpi;
                    const csvFilename = `SAS_payout_${inst.name.replace(/\s/g, '_')}_${selectedMonth}.csv`;
                    const csvContent = [
                      'Instructor,Month,Gross Earnings (JPY),WPI Deduction (JPY),Net Payout (JPY),Full Days,Half Days',
                      `"${inst.name}","${selectedMonth}",${Math.round(mo.grossEarn)},${wpi},${net},${mo.fd},${mo.hd}`,
                    ].join('\n');

                    return (
                      <tr key={inst.userId}
                        onMouseEnter={(e) => e.currentTarget.style.background = V.s2}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                        <Td style={{ color: inst.isCollective ? V.purple : V.t1 }}>{inst.name}</Td>
                        <Td mono style={{ fontSize: 11 }}>
                          {mo.fd > 0 || mo.hd > 0 ? `${mo.fd} FD · ${mo.hd} HD` : '—'}
                        </Td>
                        <Td right mono>{fmt(mo.grossEarn)}</Td>
                        <Td right mono style={{ color: V.amber }}>
                          −{fmt(wpi)} <span style={{ color: V.t3, fontSize: 11 }}>(5%)</span>
                        </Td>
                        <Td right mono bold>{fmt(net)}</Td>
                        <Td>
                          <button onClick={() => {
                            const blob = new Blob([csvContent], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = csvFilename; a.click();
                            URL.revokeObjectURL(url);
                          }} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: 'none', background: 'none', color: V.t1, cursor: 'pointer', fontFamily: F, fontWeight: 500 }}>
                            Export CSV →
                          </button>
                        </Td>
                      </tr>
                    );
                  })
                : (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: V.t3, fontSize: 12, fontStyle: 'italic' }}>No instructor earnings for {selectedMonth}</td></tr>
                )}
            </tbody>
          </table>
          <div style={{ padding: '12px 16px', fontSize: 11, color: V.t3, fontStyle: 'italic', borderTop: `1px solid ${V.border}` }}>
            Other deductions (Niseko pass, rent, uniform) not yet tracked — add manually before finalising instructor invoices. Instructor self-billing via Xero Bills API is a future enhancement.
          </div>
        </Card>
      </div>

      {/* Spinner animation */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Earnings Calculator Tab ────────────────────────────────────────────────

function CalcTab() {
  const [price, setPrice] = useState(105000);
  const [isCard, setIsCard] = useState(true);
  const [stage, setStage] = useState(1);

  const calc = calcCollectiveEarnings(price, { stage, isCard });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Card title="Collective earnings — per booking">
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Lesson price (¥, tax-inclusive)">
            <input type="number" value={price} onChange={e => setPrice(Number(e.target.value) || 0)} style={{ fontFamily: FM, fontSize: 13, padding: '8px 12px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none', width: '100%' }} />
          </Field>
          <Field label="Payment method">
            <select value={isCard ? '1' : '0'} onChange={e => setIsCard(e.target.value === '1')} style={{ fontFamily: F, fontSize: 13, padding: '8px 12px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none', width: '100%' }}>
              <option value="1">Card — 3.6% fee applies</option>
              <option value="0">Cash / bank transfer</option>
            </select>
          </Field>
          <Field label="Commission stage">
            <select value={stage} onChange={e => setStage(Number(e.target.value))} style={{ fontFamily: F, fontSize: 13, padding: '8px 12px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none', width: '100%' }}>
              <option value={1}>Stage 1 — 25% to SAS</option>
              <option value={2}>Stage 2 — 20% to SAS</option>
              <option value={3}>Stage 3 — 18% to SAS</option>
            </select>
          </Field>

          {/* Calculation breakdown */}
          <div style={{ background: V.s2, border: `1px solid ${V.border}`, borderRadius: 12, padding: 16, marginTop: 8 }}>
            <CalcLine label="Lesson price" value={fmt(calc.lessonPrice)} />
            <CalcLine label="Consumption tax (÷11)" value={`−${fmt(calc.tax)}`} />
            <CalcLine label={`Card fee (3.6%)`} value={isCard ? `−${fmt(calc.cardFee)}` : '¥0'} />
            <div style={{ borderTop: `1px solid ${V.border}`, marginTop: 6, paddingTop: 6 }}>
              <CalcLine label="Adjusted revenue" value={fmt(calc.adjusted)} bold />
            </div>
            <CalcLine label={`SAS commission (${Math.round(calc.commRate * 100)}%)`} value={`−${fmt(calc.commission)}`} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600, color: V.t1, borderTop: `1px solid ${V.border}`, paddingTop: 10, marginTop: 6 }}>
              <span>Instructor earns</span>
              <span style={{ fontFamily: FM }}>{fmt(calc.instructorEarns)}</span>
            </div>
            {/* Percentage bar */}
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: V.t3 }}>
                <span>% of lesson price retained</span>
                <span style={{ fontFamily: FM }}>{calc.pct}%</span>
              </div>
              <div style={{ height: 6, background: V.s3, borderRadius: 3, overflow: 'hidden', marginTop: 4 }}>
                <div style={{ height: '100%', background: V.green, borderRadius: 3, width: `${calc.pct}%` }} />
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: V.t3, padding: 10, background: V.s2, borderRadius: 8 }}>
            Passed work: originating instructor earns <strong>10%</strong> = <strong>{fmt(calc.passedWork)}</strong>
          </div>
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Stage comparison */}
        <Card title="Stage comparison">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr><Th>Stage</Th><Th>Rate</Th><Th right>SAS takes</Th><Th right>You earn</Th></tr></thead>
            <tbody>
              {[1, 2, 3].map(s => {
                const c = calcCollectiveEarnings(price, { stage: s, isCard });
                return <tr key={s}><Td>Stage {s}</Td><Td mono>{Math.round(c.commRate * 100)}%</Td><Td right mono>−{fmt(c.commission)}</Td><Td right mono bold>{fmt(c.instructorEarns)}</Td></tr>;
              })}
            </tbody>
          </table>
        </Card>

        {/* School day rates */}
        <Card title="School day rates">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr><Th>Level</Th><Th right>Full day</Th><Th right>Half day</Th><Th right>Non-teach/hr</Th></tr></thead>
            <tbody>
              {SCHOOL_DAY_RATES.map(r => (
                <tr key={r.level}><Td>{r.level}</Td><Td right mono>{fmt(r.fullDay)}</Td><Td right mono>{fmt(r.halfDay)}</Td><Td right mono>{fmt(r.nonTeach)}</Td></tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

// ── Shared sub-components ──────────────────────────────────────────────────

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, padding: '12px 14px', boxShadow: V.shadow }}>
      <div style={{ fontSize: 10, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: color || V.t1, lineHeight: 1, letterSpacing: '-0.03em', fontFamily: FM }}>{value}</div>
      <div style={{ fontSize: 11, color: V.t3, marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, boxShadow: V.shadow, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${V.border}`, fontSize: 13, fontWeight: 600, color: V.t1, display: 'flex', alignItems: 'center', gap: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Badge({ label, color }) {
  const colors = {
    green: { bg: V.greenBg, c: V.greenTxt }, blue: { bg: V.blueBg, c: V.blueTxt },
    purple: { bg: V.purpleSoft, c: V.purpleTxt }, amber: { bg: V.amberBg, c: V.amberTxt },
    red: { bg: V.redBg, c: V.redTxt }, gray: { bg: V.grayBg, c: V.grayTxt },
  };
  const cs = colors[color] || colors.gray;
  return <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 500, background: cs.bg, color: cs.c, whiteSpace: 'nowrap' }}>{label}</span>;
}

function Btn({ label, onClick, primary, ghost }) {
  return <button onClick={onClick} style={{ fontSize: 12, padding: '6px 14px', borderRadius: 8, border: `1px solid ${primary ? V.t1 : ghost ? 'transparent' : V.borderStrong}`, background: primary ? V.t1 : 'transparent', color: primary ? '#fff' : V.t1, cursor: 'pointer', fontFamily: F, fontWeight: 500 }}>{label}</button>;
}

function Th({ children, right, center }) {
  return <th style={{ textAlign: right ? 'right' : center ? 'center' : 'left', padding: '9px 12px', fontWeight: 500, color: V.t2, borderBottom: `1px solid ${V.border}`, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', background: V.s2 }}>{children}</th>;
}

function Td({ children, right, center, mono, bold, style: extra }) {
  return <td style={{ padding: '9px 12px', borderBottom: `1px solid ${V.border}`, color: V.t1, textAlign: right ? 'right' : center ? 'center' : 'left', fontFamily: mono ? FM : F, fontSize: mono ? 12 : 13, fontWeight: bold ? 600 : 400, ...extra }}>{children}</td>;
}

function MiniStat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 10, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, fontFamily: FM, color: color || V.t1 }}>{value}</div>
    </div>
  );
}

function CalcLine({ label, value, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0', color: V.t2 }}>
      <span>{label}</span>
      <span style={{ fontFamily: FM, fontSize: 12, fontWeight: bold ? 500 : 400 }}>{value}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: V.t2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
      {children}
    </div>
  );
}

function DeductionChip({ label, value }) {
  return (
    <div style={{ padding: '6px 10px', background: V.amberBg, borderRadius: 8 }}>
      <span style={{ color: V.amberTxt, fontSize: 10, fontWeight: 600 }}>{label}</span>
      <div style={{ fontFamily: FM, fontSize: 13, fontWeight: 500, color: V.amberTxt }}>−{value}</div>
    </div>
  );
}

export default OrgFinanceView;
